/**
 * Centre gallery images.
 *
 * A clinic can upload up to 4 photos, but most upload fewer and some upload none.
 * Every surface therefore renders a fixed number of tiles, padding whatever the
 * clinic gave us from the front of the ordered fallback list in site config
 * (`default_centre_image`). A centre with 2 photos gets defaults 1 and 2; a
 * centre with none gets all 4.
 *
 * The defaults are stored pre-cropped to 3:2, which is also the ratio every
 * gallery slot uses, so a default tile is never cropped a second time.
 */

const isUrl = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Upper bound on gallery tiles. Matches the 4-photo upload cap; a gallery
 *  renders fewer when there simply aren't that many photos or defaults. */
export const GALLERY_SIZE = 4;

/**
 * Site config holds the fallbacks in `default_centre_image`, historically a
 * single URL and now an ordered list. Accept both so an older config document
 * (or a half-migrated one) still renders.
 */
export function resolveDefaultImages(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.filter(isUrl);
  return isUrl(value) ? [value] : [];
}

/** The shape we need off a centre — kept structural so both the list and detail
 *  payloads satisfy it without importing the full Centre type. */
export interface CentreImageFields {
  images?: string[];
  image?: string;
  image2?: string;
  image3?: string;
  image4?: string;
}

/**
 * The centre's own photos, in display order, deduped.
 *
 * The `images` array is the current field; `image`/`image2`… are the legacy
 * columns and come after it so a migrated centre keeps the cover photo it had.
 */
export function ownImages(centre: CentreImageFields): string[] {
  const collected = [
    ...(centre.images ?? []),
    centre.image,
    centre.image2,
    centre.image3,
    centre.image4,
  ];
  return Array.from(new Set(collected.filter(isUrl).map((url) => url.trim())));
}

/**
 * The centre's photos padded from `defaults` up to `size` tiles.
 *
 * Defaults already used by the centre itself are skipped, so a clinic that
 * happens to have saved a default URL never sees it twice.
 */
export function galleryImages(
  centre: CentreImageFields,
  defaults: string[] | undefined,
  size: number = GALLERY_SIZE,
): string[] {
  const own = ownImages(centre);
  if (own.length >= size) return own.slice(0, size);

  const fill = (defaults ?? []).filter(isUrl).filter((url) => !own.includes(url));
  return [...own, ...fill].slice(0, size);
}

/** The single cover photo used on cards — the centre's first, else the first default. */
export function coverImage(
  centre: CentreImageFields,
  defaults: string[] | undefined,
): string | undefined {
  return galleryImages(centre, defaults, 1)[0];
}
